import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '@environments/environment';
import {
  RunReportRequest,
  ReportResultsResponse,
  SchedulesResponse,
  ScheduleResponse,
  CreateScheduleRequest,
  CreateSchedulesResponse,
  UpdateScheduleRequest,
  BulkRunReportRequest,
  BulkRunReportResponse,
} from '@models/report-template.model';

@Injectable({
  providedIn: 'root'
})
export class ReportingService {
  private readonly apiUrl = environment.apiUrl;

  constructor(private readonly http: HttpClient) {}

  getPracticeSummaryTemplate(): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/reports/practice-summary-template`, {
      responseType: 'blob'
    });
  }

  runReport(request: RunReportRequest): Observable<{ blob: Blob; fileName: string }> {
    return this.http.post(`${this.apiUrl}/reports/practice-summary-report`, request, {
      responseType: 'blob',
      observe: 'response',
    }).pipe(
      map((response: HttpResponse<Blob>) => {
        const disposition = response.headers.get('content-disposition');
        const match = disposition?.match(/filename="?([^";]+)"?/);
        const fileName = match?.[1] ?? `practice-summary-${new Date().toISOString().slice(0, 10)}.pdf`;
        return { blob: response.body!, fileName };
      })
    );
  }

  exportOrgAudit(): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/Organization/export/audit`, {
      responseType: 'blob'
    });
  }

  getReportResults(organizationId: number): Observable<ReportResultsResponse> {
    const params = new HttpParams().set('organizationId', organizationId.toString());
    return this.http.get<ReportResultsResponse>(`${this.apiUrl}/reports/results`, { params });
  }

  viewReportResult(id: number): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/reports/results/${id}/download`, {
      responseType: 'blob'
    });
  }

  downloadReportResult(id: number): Observable<{ blob: Blob; fileName: string }> {
    return this.http.get(`${this.apiUrl}/reports/results/${id}/download`, {
      responseType: 'blob',
      observe: 'response',
    }).pipe(
      map((response: HttpResponse<Blob>) => {
        const disposition = response.headers.get('content-disposition');
        const match = disposition?.match(/filename="?([^";]+)"?/);
        const fileName = match?.[1] ?? `report-${id}.pdf`;
        return { blob: response.body!, fileName };
      })
    );
  }

  deleteReportResult(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/reports/results/${id}`);
  }

  getSchedules(organizationId: number): Observable<SchedulesResponse> {
    const params = new HttpParams().set('organizationId', organizationId.toString());
    return this.http.get<SchedulesResponse>(`${this.apiUrl}/reportschedules`, { params });
  }

  getSchedule(id: number): Observable<ScheduleResponse> {
    return this.http.get<ScheduleResponse>(`${this.apiUrl}/reportschedules/${id}`);
  }

  createSchedule(request: CreateScheduleRequest): Observable<CreateSchedulesResponse> {
    return this.http.post<CreateSchedulesResponse>(`${this.apiUrl}/reportschedules`, request);
  }

  updateSchedule(id: number, request: UpdateScheduleRequest): Observable<ScheduleResponse> {
    return this.http.put<ScheduleResponse>(`${this.apiUrl}/reportschedules/${id}`, request);
  }

  deleteSchedule(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/reportschedules/${id}`);
  }

  reactivateSchedule(id: number): Observable<ScheduleResponse> {
    return this.http.post<ScheduleResponse>(`${this.apiUrl}/reportschedules/${id}/reactivate`, null);
  }

  bulkRunReport(request: BulkRunReportRequest): Observable<BulkRunReportResponse> {
    return this.http.post<BulkRunReportResponse>(`${this.apiUrl}/reports/practice-summary-report/bulk`, request);
  }
}
